import React, { FunctionComponent, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { connect, useSelector } from 'react-redux'
import { useNavigate, useOutletContext } from 'react-router'
import { useLocation } from 'react-router-dom'

import { format, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'
import { fromJS, Map as ImmutableMap } from 'immutable'
import { isEmpty } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { createAppointmentPayload } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/createAppointmentPayload'
import { getGuardianInfoForEpisode } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/utils'
import {
  DAILY_END_TIME,
  DAILY_START_TIME,
  SLICE_DURATION_MINUTES,
} from '@lyrahealth-inc/shared-app-logic/src/features/calendar/types'
import { getCountryDisplayName } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/stringUtils'
import { SessionTypeText } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/messages'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { ProviderProgramTaxonomy } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'
import { MEETING_FORMATS } from '@lyrahealth-inc/shared-app-logic/src/models/session/Session'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { SelectField } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/SelectField'
import { ChevronIcon, ChevronIconDirection } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/ChevronIcon'
import { GlobeIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/GlobeIcon'
import { LoadingIndicator } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LoadingIndicator'
import { Link } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/link/Link'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { TertiaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/tertiaryButton/TertiaryButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { ExpandedCalendarBooking } from '@lyrahealth-inc/ui-core-crossplatform/src/pages/expandedCalendarBooking/ExpandedCalendarBooking'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import {
  ScheduleSessionFullScreenHeaderConfig,
  useScheduleSessionFullScreenHeader,
} from './ScheduleSessionFullScreenLayout'
import { actions, mixpanelEvents } from '../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../mixpanel/mixpanelTracking'
import {
  APPOINTMENT_OPERATIONS,
  getMeetingFormats,
  programConfig as PROGRAM_CONFIG,
} from '../../common/constants/appConstants'
import {
  CLIENTS_NEW_SESSION,
  CLIENTS_NEW_SESSION_CONFIRMATION,
  CLIENTS_SESSIONS,
  LC_CALENDAR_CURRENT,
} from '../../common/constants/routingConstants'
import { isEngageDAProvider } from '../../common/utils/utils'
import {
  getAuthInPersonPreference,
  getAuthRoles,
  getAuthUser,
  getAuthUserId,
  getAuthVideoPreference,
} from '../../data/auth/authSelectors'
import { getClientAppointmentDetails } from '../../data/lyraTherapy/clientSelectors'
import { getLyraTherapyContentsPrograms } from '../../data/lyraTherapy/contentSelectors'
import { useAppDispatch } from '../../data/store'
import { usePreviousRoute } from '../../hooks/usePreviousRoute'
import { getProviderDetailsAddresses, getProviderModalities } from '../../providers/data/providerSelectors'
import {
  useCreateAppointmentMutation,
  useCreateRecurringAppointmentMutation,
  useGetProviderAvailabilityQuery,
  useUpdateAppointmentMutation,
} from '../clients/clientDetails/data/appointmentsApi'
import { clearSelectedAppointment, setAppointment } from '../clients/clientDetails/data/appointmentsSlice'
import { useClientAppointmentsData } from '../clients/clientDetails/data/hooks/useClientAppointmentsData'
import { useSelectedClientData } from '../clients/clientDetails/data/hooks/useSelectedClientData'
import { setToastContent } from '../data/ltToastAutoActions'
import { getLTVideoSessionOpen } from '../data/ltVideoSelectors'
import { useSelectedEpisodeForClient } from '../episodes/data/hooks/useSelectedEpisodeForClient'

const OuterContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  backgroundColor: theme.colors.backgroundPrimary,
  width: '100%',
}))

const NavButtonContainer = styled.View({
  marginBottom: '24px',
})

const RescheduleHeadingNav = styled.View<{ theme: ThemeType; videoSessionOpen: boolean }>(
  ({ theme, videoSessionOpen }) => ({
    marginTop: videoSessionOpen ? '120px' : '0px',
    paddingLeft: theme.spacing['16px'],
    width: '100%',
    borderBottomStyle: 'solid',
    borderBottomWidth: '1px',
    borderBottomColor: theme.colors.borderDefault,
    display: 'flex',
    flexDirection: 'row',
  }),
)

const AppointmentInfo = styled(BodyText)<{ theme: ThemeType }>(({ theme }) => ({
  marginBottom: theme.spacing['24px'],
}))

const DurationEditContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  height: '100px',
  alignContent: 'start',
  flexWrap: 'wrap',
  marginTop: theme.spacing['24px'],
}))

const DurationAndTypeContainer = styled.View({
  flexDirection: 'column',
  marginRight: '16px',
  justifyContent: 'end',
})

const MainContentContainer = styled.View<{ theme: ThemeType; withTopMargin?: boolean }>(({ theme, withTopMargin }) => ({
  backgroundColor: theme.colors.backgroundPrimary,
  width: '85%',
  margin: 'auto',
  ...(withTopMargin && { marginTop: theme.spacing['32px'] }),
}))

const InnerBootstrapContainer = styled.View({
  marginBottom: '50px',
})

const DisplayContainer = styled.View({
  width: '70%',
})

const AdjacentButtonContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginLeft: 'auto',
  right: 0,
  flexDirection: 'row',
  paddingBottom: theme.spacing['16px'],
  marginTop: theme.spacing['8px'],
}))

const SubmitButton = styled(PrimaryButton)<{ theme: ThemeType }>(({ theme }) => ({
  marginRight: theme.spacing['16px'],
  marginLeft: theme.spacing['16px'],
}))

const InternationalBanner = styled.View<{ theme: ThemeType }>(({ theme: { colors } }) => ({
  backgroundColor: colors.backgroundHighlightPrimary,
  padding: '16px 0 16px 16px',
  marginBottom: '16px',
  display: 'flex',
  alignItems: 'center',

  svg: {
    marginRight: '8px',
  },
}))

const LinkContainer = styled.Pressable<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  paddingTop: theme.spacing['32px'],
}))

const FieldContainer = styled.View({
  margin: 0,
  padding: 0,
})

const EditMeetingDropdownContainer = styled.View({
  flexDirection: 'row',
})

const SubmitMeetingPropertiesButton = styled(PrimaryButton)<{ theme: ThemeType }>(({ theme }) => ({
  marginRight: theme.spacing['16px'],
  marginLeft: theme.spacing['16px'],
}))

const LoadingContainer = styled.View({
  alignContent: 'center',
  flexDirection: 'column',
  flexWrap: 'wrap',
  height: '100vh',
  justifyContent: 'center',
  margin: 'auto',
})

const SessionSelectionContainer: FunctionComponent<SessionSelectionContainerProps> = ({ operation }) => {
  const dispatch = useAppDispatch()
  const { setShowSubNav, isEpisodeSelectionResolved, areProgramsLoaded } = useOutletContext<{
    setShowSubNav: (visibility: boolean) => void
    isEpisodeSelectionResolved?: boolean
    areProgramsLoaded?: boolean
  }>()
  const fullScreenHeader = useScheduleSessionFullScreenHeader()
  const location = useLocation()
  useLayoutEffect(() => {
    if (!fullScreenHeader) {
      setShowSubNav(false)
    }
  }, [fullScreenHeader, setShowSubNav])
  const user = useSelector(getAuthUser)
  const providerId: string | undefined = useSelector(getAuthUserId)
  const appointment = useSelector(getClientAppointmentDetails)
  const providerAddresses = useSelector(getProviderDetailsAddresses)
  const providerModalities = useSelector(getProviderModalities)
  const videoPreference = useSelector(getAuthVideoPreference) ?? true

  const programs = useSelector(getLyraTherapyContentsPrograms)
  const { data: selectedEpisode } = useSelectedEpisodeForClient() as { data: Episode | undefined }

  const roles: string[] = useSelector(getAuthRoles) ?? []
  const programForEpisode = programs.find((p) => p.id === selectedEpisode?.program_id)

  const programTaxonomy: ProviderProgramTaxonomy | undefined = programForEpisode?.program_taxonomy.find(
    (p) => p.role != null && roles.includes(p.role),
  )
  const isWaitingForAvailability = !isEpisodeSelectionResolved || !areProgramsLoaded

  const videoSessionOpen = useSelector(getLTVideoSessionOpen)
  const programConfig = selectedEpisode ? PROGRAM_CONFIG[selectedEpisode.program_name] : undefined

  const intl = useIntl()
  const { colors } = useTheme()
  const navigate = useNavigate()
  const { data: clientDetails } = useSelectedClientData()
  const [loading, setLoading] = useState(false)
  const [hasSelectedTime, setHasSelectedTime] = useState(false)
  const [showEditMeetingDropdowns, setShowEditMeetingDropdowns] = useState(false)
  const [selectedAppointmentDuration, setSelectedAppointmentDuration] = useState<number | undefined>(
    appointment?.appointmentDuration,
  )
  const [meetingFormat, setMeetingFormat] = useState<string | undefined>(appointment?.meetingFormat)
  const [selectedAppointmentAddress, setSelectedAppointmentAddress] = useState<string | undefined>(appointment?.address)
  const [selectedMeetingFormat, setSelectedMeetingFormat] = useState<string | undefined>(
    appointment && appointment?.meetingFormat === MEETING_FORMATS.IN_PERSON
      ? appointment.meetingFormat.concat(appointment.address)
      : appointment?.meetingFormat,
  )
  const [updateAppointment] = useUpdateAppointmentMutation()
  const [createAppointment] = useCreateAppointmentMutation()
  const [createRepeatingAppointment] = useCreateRecurringAppointmentMutation()
  const prevRoute = usePreviousRoute()

  const { data: allAppointments } = useClientAppointmentsData()
  const appointments = useMemo(
    () => allAppointments?.filter((apt) => apt.episodeId === selectedEpisode?.id),
    [allAppointments, selectedEpisode?.id],
  )

  const inPersonPreference = useSelector(getAuthInPersonPreference) ?? false

  // If any previous appointment has guardianLyraId, carry it over to the new appointment
  const guardianInfoForProgram = useMemo(
    () => getGuardianInfoForEpisode(appointments ?? [], selectedEpisode?.participants),
    [appointments, selectedEpisode?.participants],
  )

  const timeZone = Intl.DateTimeFormat()?.resolvedOptions()?.timeZone ?? 'America/Los_Angeles'

  const handleSelectedAppointment = (selectedAppointmentTime: string): any => {
    const appointmentMap = fromJS(appointment)
    const appointmentsList = fromJS(appointments)

    const date = utcToZonedTime(selectedAppointmentTime, timeZone)
    const time = format(date, 'HH:mm:ss', { timeZone })
    const dateStr = format(date, 'yyyy-MM-dd', { timeZone })

    const selectedAppointmentFormatted = {
      startDate: dateStr,
      startTime: time,
      timeZone,
    }

    switch (operation) {
      case APPOINTMENT_OPERATIONS.NEW:
        return createAppointmentPayload({
          appointment: appointmentMap.merge(selectedAppointmentFormatted),
          providerID: providerId,
          providerModalities,
          userID: clientDetails?.id,
          referenceAppointment: appointmentsList.get(-1, ImmutableMap()),
        })
      case APPOINTMENT_OPERATIONS.RESCHEDULE:
        return {
          ...appointment,
          ...selectedAppointmentFormatted,
          appointmentStatus: 'rescheduled',
        }
    }
  }

  const { data: providerAvailability, isLoading } = useGetProviderAvailabilityQuery(
    {
      providerId: providerId,
      daysToFetch: 90,
      params: {
        slice_duration_minutes: SLICE_DURATION_MINUTES,
        daily_start_time: DAILY_START_TIME,
        daily_end_time: DAILY_END_TIME,
        clientele: programTaxonomy?.clientele ?? '',
        treatment: programTaxonomy?.treatment ?? '',
        partner: programTaxonomy?.partner ?? '',
        offering: programTaxonomy?.offering ?? '',
        appointment_class: appointments?.some((a) => a.appointmentStatus === 'completed') ? 'recurring' : 'initial',
        lead_time_hours: 0,
        appointment_duration_minutes: appointment?.appointmentDuration,
      },
    },
    {
      skip: isWaitingForAvailability,
      refetchOnMountOrArgChange: true,
    },
  )

  const addressMap = useMemo(() => {
    const addressMapping = new Map<string, { locationId?: number; addressForAppointment?: string }>()
    let currAddress = 0
    providerAddresses?.forEach((address) => {
      const appointmentAddress = [address.street1, address.city, address.state].filter(Boolean).join(', ')
      addressMapping.set(
        MEETING_FORMATS.IN_PERSON.concat(appointmentAddress !== '' ? appointmentAddress : currAddress.toString()),
        {
          // At present locationId is not needed nor required for appointments but leaving it in here in case we need
          // more strict data at any point in the near future.
          locationId: address.id,
          addressForAppointment: appointmentAddress,
        },
      )
      currAddress++
    })
    return addressMapping
  }, [providerAddresses])

  const goBack = useCallback(() => {
    setShowSubNav(true)
    switch (operation) {
      case APPOINTMENT_OPERATIONS.NEW:
        navigate(CLIENTS_NEW_SESSION.route)
        break
      case APPOINTMENT_OPERATIONS.RESCHEDULE:
        const navTarget =
          location?.state?.returnRoute ??
          (location?.state?.entryPoint === 'In Product Calendar' ? LC_CALENDAR_CURRENT.route : CLIENTS_SESSIONS.route)
        navigate(navTarget)
        break
    }
  }, [operation, navigate, location, setShowSubNav])

  const headlineText = useMemo(() => {
    switch (operation) {
      case APPOINTMENT_OPERATIONS.NEW:
        return 'Create new'
      case APPOINTMENT_OPERATIONS.RESCHEDULE:
        return 'Reschedule'
      case APPOINTMENT_OPERATIONS.CANCEL:
        return 'Cancel'
    }
  }, [operation])

  const onTimeSelection = (dateTime: string) => {
    const appointment = handleSelectedAppointment(dateTime)
    if (appointment) {
      dispatch(setAppointment(appointment))
      switch (operation) {
        case APPOINTMENT_OPERATIONS.NEW:
          if (fullScreenHeader) {
            setHasSelectedTime(true)
          } else if (appointment.appointmentId) {
            // Navigate with appointment ID if available
            navigate(`${CLIENTS_NEW_SESSION_CONFIRMATION.route}/${appointment.appointmentId}`)
          } else {
            navigate(CLIENTS_NEW_SESSION_CONFIRMATION.route)
          }
          break
        case APPOINTMENT_OPERATIONS.RESCHEDULE:
          setHasSelectedTime(true)
          break
      }
    }
  }

  // Full-screen flow creates the appointment directly
  const createNewSession = useCallback(() => {
    if (!appointment || !selectedEpisode) {
      return
    }
    setLoading(true)
    const { repeatFor, sessionFrequency } = appointment as Appointment & {
      repeatFor?: number
      sessionFrequency?: string
    }
    const sessionCount = repeatFor || 0
    const appointmentCreationFunction = sessionCount > 1 ? createRepeatingAppointment : createAppointment
    const appointmentWithRequiredFields = {
      ...appointment,
      episodeId: selectedEpisode?.id,
      episodeState: selectedEpisode.state,
      episodeEndDate: selectedEpisode.end_date,
      providerPatientId: selectedEpisode?.provider_patient_id,
      programId: selectedEpisode?.program_id,
      ...(guardianInfoForProgram.guardianLyraId ? guardianInfoForProgram : {}),
    }
    appointmentCreationFunction({
      providerId,
      data: {
        ...appointmentWithRequiredFields,
        appointment: appointmentWithRequiredFields,
        episodeId: selectedEpisode?.id,
        providerPatientId: selectedEpisode?.provider_patient_id,
        intervalDays: sessionFrequency ? Number(sessionFrequency) : 0,
        sessionCount,
        programId: selectedEpisode?.program_id,
      },
    })
      .unwrap()
      .then(() => {
        track({ event: mixpanelEvents.CONFIRM_SCHEDULE_NEW_SESSION })
        dispatch(clearSelectedAppointment())
        fullScreenHeader?.exitToOrigin()
        dispatch(
          setToastContent({
            text: 'Done! Successfully scheduled the appointment',
            id: 'SessionSelectionContainer-Toast-Success',
            toastType: 'success',
          }),
        )
      })
      .catch(() => {
        setLoading(false)
        dispatch(
          setToastContent({
            text: 'Failed to schedule the appointment. Please try again.',
            id: 'SessionSelectionContainer-Toast-Error',
            toastType: 'error',
          }),
        )
      })
  }, [
    appointment,
    selectedEpisode,
    guardianInfoForProgram,
    providerId,
    createAppointment,
    createRepeatingAppointment,
    dispatch,
    fullScreenHeader,
  ])

  // Register the full-screen header's "Create session" button + back arrow with the shared layout.
  useEffect(() => {
    if (!fullScreenHeader) {
      return
    }
    const config: ScheduleSessionFullScreenHeaderConfig = {
      primaryLabel: 'Create session',
      onPrimary: createNewSession,
      primaryDisabled: !hasSelectedTime || loading,
      primaryLoading: loading,
      onBack: goBack,
    }
    fullScreenHeader.setHeader(config)
  }, [fullScreenHeader, hasSelectedTime, loading, createNewSession, goBack])

  const handleSubmit = () => {
    setLoading(true)
    if (appointment && selectedEpisode) {
      if (isEngageDAProvider(roles)) {
        track({
          event: mixpanelEvents.CONFIRM_RESCHEDULE_SESSION,
          details: {
            appointmentId: appointment.appointmentId,
          },
        })
      } else {
        track({
          event: mixpanelEvents.BUTTON_PRESS,
          action: actions.RESCHEDULE_SESSION,
          details: {
            appointmentId: appointment.appointmentId,
            entryPoint: prevRoute?.includes('calendar') ? 'In Product Calendar' : 'Sessions tab',
          },
        })
      }
      updateAppointment({
        providerId: providerId,
        data: {
          // Only add guardian info for parent-led child programs (Engage DA child therapy)
          // DirectAccessIndividualAdolescentTherapy is teen-led (self-managed), so no guardian info needed
          ...appointment,
          ...guardianInfoForProgram,
          programId: selectedEpisode?.program_id,
        },
      })
        .unwrap()
        .then(() => {
          if (operation === APPOINTMENT_OPERATIONS.RESCHEDULE) {
            navigate(-1)
          } else {
            navigate(CLIENTS_SESSIONS.route)
          }
        })
        .catch(() => {
          setLoading(false)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }
  const banner =
    !isEmpty(clientDetails?.country) && clientDetails?.country !== 'US' ? (
      <InternationalBanner testID={tID('SessionSelectionContainer-international-banner')}>
        <GlobeIcon />{' '}
        {`Note that the session time is in your timezone, but that the client is located in ${getCountryDisplayName(
          clientDetails?.country ?? '',
        )}, ${clientDetails?.time_zone}.`}
      </InternationalBanner>
    ) : null
  const appointmentStartZonedToUTC =
    appointment?.startDate &&
    appointment?.startTime &&
    zonedTimeToUtc(
      new Date(`${appointment?.startDate} ${appointment?.startTime}`),
      appointment?.timeZone ?? 'America/Los_Angeles',
    )
  const appointmentDate = appointmentStartZonedToUTC && format(appointmentStartZonedToUTC, 'EEEE, MMM d, h:mm a zzz')

  const liveMessagingEnabled = clientDetails?.live_messaging ?? false
  const isLiveMessagingEnabled = (liveMessagingEnabled && user?.text_sessions_enabled) || false
  const exclusions = {
    exclusions: [
      ...(!isEngageDAProvider(roles) || videoPreference ? [] : [MEETING_FORMATS.VIDEO]),
      ...(isLiveMessagingEnabled ? [] : [MEETING_FORMATS.LIVE_MESSAGING]),
      ...(inPersonPreference ? [] : [MEETING_FORMATS.IN_PERSON]),
      ...(isEngageDAProvider(roles) ? [MEETING_FORMATS.PHONE] : []),
    ],
  }
  const enabledMeetingFormats = getMeetingFormats(exclusions)

  if (isWaitingForAvailability || isLoading || loading) {
    return (
      <LoadingContainer>
        <LoadingIndicator size={45} topPadding={0} />
      </LoadingContainer>
    )
  }

  return (
    <OuterContainer>
      {!fullScreenHeader && (
        <RescheduleHeadingNav testID={tID('SessionSelectionContainer-heading')} videoSessionOpen={videoSessionOpen}>
          <DisplayContainer>
            <Title
              text={`${headlineText} session ${appointment?.sessionNumber ?? ''} with ${clientDetails?.full_name}`}
              size={TitleSize.LARGE}
              color={colors.textPrimary}
            />
            {appointmentDate && (
              <AppointmentInfo
                testID={tID('SessionCancellation-appointmentDate')}
                color={colors.textInactive}
                text={appointmentDate}
              />
            )}
          </DisplayContainer>
          <AdjacentButtonContainer>
            <TertiaryButton
              text='Cancel'
              onPress={() => goBack()}
              style={{
                borderWidth: 0,
                display: 'flex',
                height: 48,
                flexDirection: 'row',
                alignItems: 'center',
              }}
            />
            <SubmitButton
              testID={tID('SessionSelectionContainer-SelectTimeslot')}
              text='Select timeslot'
              disabled={
                isWaitingForAvailability ||
                (operation === APPOINTMENT_OPERATIONS.RESCHEDULE && !hasSelectedTime) ||
                isLoading ||
                loading
              }
              onPress={handleSubmit}
            />
          </AdjacentButtonContainer>
        </RescheduleHeadingNav>
      )}
      <MainContentContainer withTopMargin={!!fullScreenHeader}>
        <InnerBootstrapContainer>
          {!fullScreenHeader &&
            (operation === APPOINTMENT_OPERATIONS.NEW ? (
              <NavButtonContainer>
                <LinkContainer testID={tID('SessionSelectionContainer-backto-define-session-details')} onPress={goBack}>
                  <ChevronIcon
                    fillColor={colors.primaryButtonBackgroundHover}
                    size={20}
                    direction={ChevronIconDirection.LEFT}
                  />
                  <Link text='Define session details' onPress={goBack} />
                </LinkContainer>
              </NavButtonContainer>
            ) : (
              <DurationEditContainer>
                <DurationAndTypeContainer>
                  {!showEditMeetingDropdowns && <BodyText text='Duration' size={BodyTextSize.DEFAULT} />}
                  {showEditMeetingDropdowns && programConfig ? (
                    <FieldContainer>
                      <SelectField
                        label='Duration'
                        name='meetingDuration'
                        options={programConfig?.appointmentDurationOptions.map((number: number) => {
                          return { label: `${number} minutes`, value: number }
                        })}
                        value={selectedAppointmentDuration ?? ''}
                        onChange={(value: number) => {
                          setSelectedAppointmentDuration(value)
                        }}
                        baseInputStyle={{ marginBottom: 0, width: 140 }}
                      />
                    </FieldContainer>
                  ) : (
                    <BodyText text={`${appointment?.appointmentDuration} minutes`} color={colors.textSecondary} />
                  )}
                </DurationAndTypeContainer>
                {appointment?.meetingFormat && (
                  <DurationAndTypeContainer>
                    {!showEditMeetingDropdowns && <BodyText text='Type' size={BodyTextSize.DEFAULT} />}

                    {showEditMeetingDropdowns ? (
                      <FieldContainer>
                        <SelectField
                          label='Type'
                          name='meetingFormat'
                          options={enabledMeetingFormats.flatMap((option: { value: string; text: string }) => {
                            if (option.value === MEETING_FORMATS.IN_PERSON && providerAddresses) {
                              const inPersonOptions: { label: string; value: string }[] = []
                              let currAddress = 0
                              providerAddresses?.forEach((address) => {
                                //These fallback options need to exist since street can technically be undefined, but it would
                                //mark a more serious issue in the data so these example text are really filler.
                                const appointmentAddress = [address.street1, address.city, address.state]
                                  .filter(Boolean)
                                  .join(', ')
                                inPersonOptions.push({
                                  label: address.street1 || 'In-person Location '.concat(currAddress.toString()),
                                  value: MEETING_FORMATS.IN_PERSON.concat(
                                    appointmentAddress !== '' ? appointmentAddress : currAddress.toString(),
                                  ),
                                })
                                currAddress++
                              })
                              return inPersonOptions
                            }
                            return { label: option.text, value: option.value }
                          })}
                          value={selectedMeetingFormat ?? ''}
                          onChange={(value: string) => {
                            if (value.includes(MEETING_FORMATS.IN_PERSON) && providerAddresses) {
                              setMeetingFormat(MEETING_FORMATS.IN_PERSON)
                              setSelectedAppointmentAddress(addressMap.get(value)?.addressForAppointment)
                            } else {
                              setMeetingFormat(value)
                              setSelectedAppointmentAddress(undefined)
                            }
                            setSelectedMeetingFormat(value)
                          }}
                          baseInputStyle={{ marginBottom: 0, width: 170 }}
                        />
                      </FieldContainer>
                    ) : (
                      // Shows the location on Engage in-person session or SessionTypeText in any other case
                      <BodyText
                        testID={tID('SessionSelectionContainer-meetingFormat')}
                        text={
                          selectedMeetingFormat?.includes(MEETING_FORMATS.IN_PERSON) &&
                          providerAddresses &&
                          !!appointment?.address
                            ? [SessionTypeText[appointment?.meetingFormat], appointment?.address]
                                .filter(Boolean)
                                .join(' - ')
                            : SessionTypeText[appointment?.meetingFormat]
                        }
                        style={{ textTransform: 'capitalize' }}
                        color={colors.textSecondary}
                      />
                    )}
                  </DurationAndTypeContainer>
                )}
                <DurationAndTypeContainer>
                  {showEditMeetingDropdowns && appointment ? (
                    <EditMeetingDropdownContainer>
                      <TertiaryButton
                        text='Cancel'
                        onPress={() => setShowEditMeetingDropdowns(false)}
                        style={{
                          borderWidth: 0,
                          display: 'flex',
                          height: 48,
                          flexDirection: 'row',
                          alignItems: 'center',
                        }}
                      />
                      <SubmitMeetingPropertiesButton
                        testID={tID('SessionSelectionContainer-SubmitMeetingProperties')}
                        text='Confirm'
                        disabled={!selectedMeetingFormat}
                        onPress={() => {
                          setShowEditMeetingDropdowns(false)
                          dispatch(
                            setAppointment({
                              ...appointment,
                              appointmentDuration: selectedAppointmentDuration ?? appointment.appointmentDuration,
                              meetingFormat: (meetingFormat ?? appointment.meetingFormat) as MEETING_FORMATS,
                              //If not in-person we want to reset the address to empty.
                              address: selectedAppointmentAddress ?? '',
                            }),
                          )
                        }}
                      />
                    </EditMeetingDropdownContainer>
                  ) : (
                    <Link
                      testID={tID('SessionSelectionContainer-EditMeetingProperties')}
                      text='Edit'
                      onPress={() => {
                        setShowEditMeetingDropdowns(true)
                      }}
                    />
                  )}
                </DurationAndTypeContainer>
              </DurationEditContainer>
            ))}
          {providerAvailability && (
            <ExpandedCalendarBooking
              timeZone={timeZone}
              availabilities={providerAvailability}
              onSelected={onTimeSelection}
              intl={intl}
              responsive={true}
              banner={banner}
              sliceDurationMinutes={SLICE_DURATION_MINUTES}
              shouldShowSelected={true}
            />
          )}
        </InnerBootstrapContainer>
      </MainContentContainer>
    </OuterContainer>
  )
}

type SessionSelectionContainerProps = {
  operation: APPOINTMENT_OPERATIONS
}

const connector = connect(null, null)

export default connector(SessionSelectionContainer)
